import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { FormGroup } from 'reactstrap';
import { Input } from '@availity/form';
import './StateSelectField.scss';
import {
    LocationStateItem,
    stateToCodeMapper,
    stateCodeToNameMapper,
    getAllStatesIndexed,
} from '../../../utils/locationsHelper';
import { useStateSelector } from '../../../store/selectors';
import { useDispatch } from 'react-redux';
import { GetStatesByRegion } from '../../../slices/locations-slice/thunks/get-states-by-region.thunk';

export interface StateSelectFieldProps {
    name: string;
    value?: string;
    disabled?: boolean;
    label?: string;
    placeholder?: string;
    className?: string;
    showLongFormat?: boolean;
    style?: React.CSSProperties;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const StateSelectField: React.FC<StateSelectFieldProps> = ({
    name,
    value,
    disabled = false,
    label = 'State',
    placeholder = '..',
    className,
    showLongFormat = false,
    style,
    onChange,
}) => {
    const dispatch = useDispatch() as any;
    const statesByRegion = useStateSelector(
        (state) => state.locationsState.statesByRegion
    );

    const [internalStates, setInternalStates] = useState<LocationStateItem[]>(
        []
    );

    useEffect(() => {
        dispatch(GetStatesByRegion());
    }, [dispatch]);

    useEffect(() => {
        if (statesByRegion) {
            const indexedStates = getAllStatesIndexed(statesByRegion);
            setInternalStates(indexedStates);
        }
    }, [statesByRegion]);

    // Convert value to code if it's in long format (only when controlled)
    const normalizedValue = useMemo(() => {
        if (value === undefined) return undefined; // Let form context handle it
        if (!value) return value;
        // If value is already a code (2 chars), keep it; otherwise convert to code
        return value.length > 2 ? stateToCodeMapper(value) : value;
    }, [value]);

    // Wrap onChange to normalize incoming values
    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            // If the value from the form is in long format, normalize it
            const eventValue = event.target.value;
            if (eventValue && eventValue.length > 2) {
                // Create a modified event with normalized value
                const normalizedEvent = {
                    ...event,
                    target: {
                        ...event.target,
                        value: stateToCodeMapper(eventValue),
                    },
                } as React.ChangeEvent<HTMLInputElement>;
                onChange?.(normalizedEvent);
            } else {
                onChange?.(event);
            }
        },
        [onChange]
    );

    // Conditionally pass value prop: only include it when explicitly provided,
    // otherwise let form context manage the value via the 'name' property
    const inputProps = {
        type: 'select' as const,
        name,
        placeholder,
        disabled,
        onChange: handleChange,
        style,
        ...(normalizedValue !== undefined && { value: normalizedValue }),
    };

    return (
        <FormGroup className={className}>
            {label && <label className="copy-name-label">{label}</label>}
            <Input {...inputProps}>
                {[{ id: -1, name: '', code: '' }, ...internalStates].map(
                    (item) => {
                        // Handle empty/select option
                        if (!item.code) {
                            return (
                                <option value="" key={`state${item.id}`}>
                                    {placeholder}
                                </option>
                            );
                        }

                        // Display long format (e.g., "California") or short code (e.g., "CA")
                        const displayValue = showLongFormat
                            ? stateCodeToNameMapper(item.code)
                            : item.code;

                        return (
                            <option value={item.code} key={`state${item.id}`}>
                                {displayValue}
                            </option>
                        );
                    }
                )}
            </Input>
        </FormGroup>
    );
};

export default StateSelectField;
